import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import LayoutDashboard from '../../../components/layout/layout-dashboard';
import { Search, Users, ArrowRight, ChevronLeft, Linkedin, Mail, Clock, BarChart2 } from 'lucide-react';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';

interface ActivityLog {
  id: string;
  user_id: string;
  user_email: string | null;
  auth_provider: string | null;
  event_type: string;
  parcours_id: string | null;
  parcours_slug: string | null;
  parcours_company_name: string | null;
  step_title: string | null;
  page_path: string | null;
  created_at: string;
}

interface CandidatRow {
  userId: string;
  email: string;
  provider: string | null;
  eventCount: number;
  parcoursCount: number;
  lastActivity: string;
  lastParcours: string | null;
}

function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function isLast7Days(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;
}

function ProviderBadge({ provider }: { provider: string | null }) {
  if (provider === 'linkedin_oidc') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0077b5]/10 px-2.5 py-0.5 text-xs font-semibold text-[#0077b5]">
        <Linkedin className="w-3 h-3" />
        LinkedIn
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-600">
      <Mail className="w-3 h-3" />
      {provider === 'email' ? 'Email' : (provider ?? 'Inconnu')}
    </span>
  );
}

export default function CandidatsListAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<'all' | 'linkedin_oidc' | 'email'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | '7days'>('all');

  // Guard: admin/editor only
  useEffect(() => {
    if (!user) { navigate('/'); return; }
    supabase.from('users').select('role').eq('id', user.id).maybeSingle()
      .then(({ data }) => {
        if (!data || !['admin', 'editor'].includes(data.role)) {
          navigate('/');
        } else {
          setUserRole(data.role);
        }
      });
  }, [user?.id]);

  useEffect(() => {
    if (!userRole) return;
    supabase
      .from('candidate_activity_logs')
      .select('id,user_id,user_email,auth_provider,event_type,parcours_id,parcours_slug,parcours_company_name,step_title,page_path,created_at')
      .order('created_at', { ascending: false })
      .limit(2000)
      .then(({ data, error }) => {
        if (!error && data) setLogs(data as ActivityLog[]);
        setLoading(false);
      });
  }, [userRole]);

  // Aggregate per user
  const candidats: CandidatRow[] = useMemo(() => {
    const map = new Map<string, CandidatRow>();
    for (const log of logs) {
      const existing = map.get(log.user_id);
      if (!existing) {
        map.set(log.user_id, {
          userId: log.user_id,
          email: log.user_email ?? log.user_id,
          provider: log.auth_provider,
          eventCount: 1,
          parcoursCount: log.parcours_id ? 1 : 0,
          lastActivity: log.created_at,
          lastParcours: log.parcours_company_name,
        });
      } else {
        existing.eventCount += 1;
        if (log.parcours_id && !logs.slice(0, logs.indexOf(log)).some(
          l => l.user_id === log.user_id && l.parcours_id === log.parcours_id
        )) {
          // approximate distinct parcours
        }
        if (log.created_at > existing.lastActivity) {
          existing.lastActivity = log.created_at;
          existing.lastParcours = log.parcours_company_name;
        }
      }
    }

    // Count distinct parcours per user properly
    const parcoursPerUser = new Map<string, Set<string>>();
    for (const log of logs) {
      if (!log.parcours_id) continue;
      if (!parcoursPerUser.has(log.user_id)) parcoursPerUser.set(log.user_id, new Set());
      parcoursPerUser.get(log.user_id)!.add(log.parcours_id);
    }
    for (const [userId, row] of map) {
      row.parcoursCount = parcoursPerUser.get(userId)?.size ?? 0;
    }

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
    );
  }, [logs]);

  const filtered = useMemo(() => {
    return candidats.filter((c) => {
      if (search) {
        const q = search.toLowerCase();
        const matchEmail = c.email.toLowerCase().includes(q);
        const matchParcours = (c.lastParcours ?? '').toLowerCase().includes(q);
        if (!matchEmail && !matchParcours) return false;
      }
      if (providerFilter !== 'all') {
        const prov = c.provider ?? 'email';
        if (providerFilter === 'email' && prov !== 'email') return false;
        if (providerFilter === 'linkedin_oidc' && prov !== 'linkedin_oidc') return false;
      }
      if (dateFilter === 'today' && !isToday(c.lastActivity)) return false;
      if (dateFilter === '7days' && !isLast7Days(c.lastActivity)) return false;
      return true;
    });
  }, [candidats, search, providerFilter, dateFilter]);

  if (!userRole) return null;

  return (
    <LayoutDashboard>
      <Helmet>
        <title>Journal candidats | Admin recruteurs.io</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-[#f7f7f8]">
        {/* Header */}
        <div className="bg-white border-b border-neutral-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/parcours"
              className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Admin
            </Link>
            <span className="text-neutral-300">/</span>
            <span className="text-sm font-semibold text-neutral-800">Journal candidats</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Users className="w-4 h-4" />
            <span>{candidats.length} candidat{candidats.length > 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Candidats', value: candidats.length, icon: <Users className="w-5 h-5 text-[#d03484]" /> },
              { label: 'Événements', value: logs.length, icon: <BarChart2 className="w-5 h-5 text-[#d47254]" /> },
              { label: 'Actifs aujourd\'hui', value: candidats.filter(c => isToday(c.lastActivity)).length, icon: <Clock className="w-5 h-5 text-emerald-500" /> },
              { label: 'Via LinkedIn', value: candidats.filter(c => c.provider === 'linkedin_oidc').length, icon: <Linkedin className="w-5 h-5 text-[#0077b5]" /> },
            ].map(({ label, value, icon }) => (
              <div key={label} className="bg-white rounded-xl border border-neutral-100 p-4 flex items-center gap-3">
                {icon}
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{value}</p>
                  <p className="text-xs text-neutral-500">{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-neutral-100 p-4 mb-6 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Rechercher par email ou parcours…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d03484]/20 focus:border-[#d03484]"
              />
            </div>

            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value as typeof providerFilter)}
              className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#d03484]/20"
            >
              <option value="all">Tous les providers</option>
              <option value="linkedin_oidc">LinkedIn</option>
              <option value="email">Email/Password</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
              className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#d03484]/20"
            >
              <option value="all">Toute période</option>
              <option value="today">Aujourd'hui</option>
              <option value="7days">7 derniers jours</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
            {loading ? (
              <div className="p-8 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 bg-neutral-50 rounded animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-20 text-center">
                <Users className="w-10 h-10 text-neutral-200 mx-auto mb-3" />
                <p className="text-neutral-500 font-medium">Aucune activité candidat pour le moment.</p>
                <p className="text-neutral-400 text-sm mt-1">Les visites de parcours apparaîtront ici.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 text-left">
                    <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Email</th>
                    <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Provider</th>
                    <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Événements</th>
                    <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Parcours</th>
                    <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Dernier parcours</th>
                    <th className="px-5 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Dernière activité</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {filtered.map((c) => (
                    <tr key={c.userId} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-5 py-3.5 font-medium text-neutral-800 max-w-xs truncate">
                        {c.email}
                      </td>
                      <td className="px-5 py-3.5">
                        <ProviderBadge provider={c.provider} />
                      </td>
                      <td className="px-5 py-3.5 text-neutral-600 tabular-nums">{c.eventCount}</td>
                      <td className="px-5 py-3.5 text-neutral-600 tabular-nums">{c.parcoursCount}</td>
                      <td className="px-5 py-3.5 text-neutral-600 max-w-[160px] truncate">
                        {c.lastParcours ?? '—'}
                      </td>
                      <td className="px-5 py-3.5 text-neutral-500 whitespace-nowrap">
                        {fmtDate(c.lastActivity)}
                      </td>
                      <td className="px-5 py-3.5">
                        <Link
                          to={`/admin/candidats/${c.userId}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#d03484] hover:text-[#d47254] transition-colors"
                        >
                          Voir l'activité
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </LayoutDashboard>
  );
}
